import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { createDonationOperation } from '../operations/create-donation-operation';
import { CreateDonationDto } from '../../../api/types/donations/donor-institution';
import {
  DonorInstitutionEntity,
  DonorInstitutionWithDonations,
} from '../../../api/types/donations/donor-institution';

interface AddDonationDialogProps {
  visible: boolean;
  donor: DonorInstitutionEntity | DonorInstitutionWithDonations | null;
  onHide: () => void;
  onSuccess?: () => void;
}

export const AddDonationDialog: React.FC<AddDonationDialogProps> = ({
  visible,
  donor,
  onHide,
  onSuccess,
}) => {
  const [amount, setAmount] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string>('ILS');
  const [purpose, setPurpose] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<
    'bank_transfer' | 'check' | 'credit_card' | null
  >(null);
  const [paymentDate, setPaymentDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const paymentMethodOptions = [
    { label: 'העברה בנקאית', value: 'bank_transfer' },
    { label: "צ'ק", value: 'check' },
    { label: 'כרטיס אשראי', value: 'credit_card' },
  ];

  const currencyOptions = [
    { label: '₪ ILS', value: 'ILS' },
    { label: '$ USD', value: 'USD' },
    { label: '€ EUR', value: 'EUR' },
  ];

  const resetForm = () => {
    setAmount(null);
    setCurrency('ILS');
    setPurpose('');
    setPaymentMethod(null);
    setPaymentDate(null);
    setNotes('');
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleSubmit = async () => {
    if (!donor) return;

    const donationData: CreateDonationDto = {
      amount: amount || 0,
      currency: currency || 'ILS',
      purpose: purpose || null,
      paymentMethod: paymentMethod || null,
      paymentDate: paymentDate || new Date(),
      notes: notes || null,
    };

    await createDonationOperation(
      donor.id,
      donationData,
      setLoading,
      handleClose,
      onSuccess,
    );
  };

  const isValid = amount !== null && amount > 0 && paymentDate !== null;

  return (
    <Dialog
      header={`הוספת תרומה - ${donor?.name || ''}`}
      visible={visible}
      onHide={handleClose}
      style={{ width: '500px', direction: 'rtl' }}
      footer={
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            severity="secondary"
            text
            onClick={handleClose}
          />
          <Button
            label="הוספה"
            icon="pi pi-check"
            loading={loading}
            onClick={handleSubmit}
            disabled={!isValid}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="p-field">
          <label
            htmlFor="amount"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
            }}
          >
            סכום *
          </label>
          <InputNumber
            id="amount"
            value={amount}
            onValueChange={(e) => setAmount(e.value)}
            mode="currency"
            currency="NIS"
            locale="he-IL"
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', textAlign: 'right' }}
            showButtons
            min={0}
            maxFractionDigits={2}
          />
        </div>

        <div className="p-field">
          <label
            htmlFor="currency"
            style={{ display: 'block', marginBottom: '0.5rem' }}
          >
            מטבע
          </label>
          <Dropdown
            id="currency"
            value={currency}
            options={currencyOptions}
            onChange={(e) => setCurrency(e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="בחר מטבע"
            style={{ width: '100%' }}
          />
        </div>

        <div className="p-field">
          <label
            htmlFor="paymentDate"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 600,
            }}
          >
            תאריך תשלום *
          </label>
          <Calendar
            id="paymentDate"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.value as Date)}
            dateFormat="dd/mm/yy"
            showIcon
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', textAlign: 'right' }}
          />
        </div>

        <div className="p-field">
          <label
            htmlFor="paymentMethod"
            style={{ display: 'block', marginBottom: '0.5rem' }}
          >
            אמצעי תשלום
          </label>
          <Dropdown
            id="paymentMethod"
            value={paymentMethod}
            options={paymentMethodOptions}
            onChange={(e) => setPaymentMethod(e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder="בחר אמצעי תשלום"
            style={{ width: '100%' }}
          />
        </div>

        <div className="p-field">
          <label
            htmlFor="purpose"
            style={{ display: 'block', marginBottom: '0.5rem' }}
          >
            מטרה
          </label>
          <InputText
            id="purpose"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            style={{ width: '100%' }}
            placeholder="הזן מטרת התרומה"
          />
        </div>

        <div className="p-field">
          <label
            htmlFor="notes"
            style={{ display: 'block', marginBottom: '0.5rem' }}
          >
            הערות
          </label>
          <InputText
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ width: '100%' }}
            placeholder="הזן הערות"
          />
        </div>
      </div>
    </Dialog>
  );
};
